/**
 * MoneyInput — digits + one decimal while focused; thousands + .00 on blur only.
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type InputHTMLAttributes,
} from 'react'
import { formatMoneyBlurValue, moneyDraftFromDisplay, sanitizeMoneyDraftWithCaret } from '../lib/moneyInputCore'

export interface MoneyInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  /** Override blur formatting (e.g. SOV labels like "included"). */
  formatOnBlur?: (raw: string) => string
}

const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  {
    value,
    onChange,
    formatOnBlur,
    className,
    onFocus,
    onBlur,
    inputMode = 'decimal',
    type = 'text',
    ...rest
  },
  ref,
) {
  const innerRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => innerRef.current as HTMLInputElement)

  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState('')

  useEffect(() => {
    if (!focused) setDraft('')
  }, [value, focused])

  const shown = focused ? draft : value

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setDraft(moneyDraftFromDisplay(value))
    setFocused(true)
    onFocus?.(e)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target
    const caret = el.selectionStart ?? el.value.length
    const { value: next, caret: nextCaret } = sanitizeMoneyDraftWithCaret(el.value, caret)
    setDraft(next)
    requestAnimationFrame(() => {
      const node = innerRef.current
      if (node && node === document.activeElement) {
        node.setSelectionRange(nextCaret, nextCaret)
      }
    })
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const format = formatOnBlur ?? formatMoneyBlurValue
    const next = draft.trim() === '' ? '' : format(draft)
    setFocused(false)
    onChange(next)
    onBlur?.(e)
  }

  return (
    <input
      {...rest}
      ref={innerRef}
      type={type}
      inputMode={inputMode}
      className={className}
      value={shown}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  )
})

export default MoneyInput
